import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Protocol names of the API's csrf guard (apps/api/src/middleware/csrf.middleware.ts), not UI copy.
const CSRF_COOKIE = 'csrf-token';
const CSRF_HEADER = 'x-csrf-token';
const CSRF_REJECTIONS = ['CSRF token missing', 'CSRF token invalid'];
const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
// An authenticated GET on a router mounted after the API's csrf issuer (app.ts) makes it issue a fresh
// csrf-token cookie; /me/profile qualifies and is open to every role.
const CSRF_REFRESH_PATH = '/me/profile';

/** Read per request, never cached: the cookie of the previous user can linger after a user switch. */
function readCsrfCookie(): string | null {
  const entry = document.cookie.split('; ').find((c) => c.startsWith(`${CSRF_COOKIE}=`));
  return entry ? entry.slice(CSRF_COOKIE.length + 1) : null;
}

// One in-flight cookie refresh shared by all writes that hit a csrf-403 at the same time.
let csrfRefresh: Promise<unknown> | null = null;

// Request interceptor: attach access token and, on writes, the csrf token
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (WRITE_METHODS.includes(config.method?.toUpperCase() ?? '')) {
    const csrf = readCsrfCookie();
    if (csrf) {
      config.headers[CSRF_HEADER] = csrf;
    }
  }
  return config;
});

// Response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(undefined);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _csrfRetry?: boolean;
    };

    // Stale or missing csrf token on a write: refresh the cookie with one GET, repeat the write once.
    const apiMessage = (error.response?.data as { error?: { message?: string } } | undefined)?.error
      ?.message;
    if (
      error.response?.status === 403 &&
      !originalRequest._csrfRetry &&
      WRITE_METHODS.includes(originalRequest.method?.toUpperCase() ?? '') &&
      CSRF_REJECTIONS.includes(apiMessage ?? '')
    ) {
      originalRequest._csrfRetry = true;
      csrfRefresh ??= apiClient
        .get(CSRF_REFRESH_PATH)
        .catch(() => undefined)
        .finally(() => {
          csrfRefresh = null;
        });
      await csrfRefresh;
      // The request interceptor sets the header again only from a present cookie.
      delete originalRequest.headers[CSRF_HEADER];
      return apiClient(originalRequest);
    }

    // Skip retry for refresh endpoint itself to avoid loops
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => apiClient(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post('/api/v1/auth/refresh', null, {
          withCredentials: true,
        });
        const { accessToken, user } = response.data?.data ?? {};

        if (accessToken) {
          useAuthStore.getState().setAuth(user, accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        useAuthStore.getState().logout();
        // Only redirect if not already on the login page to prevent redirect loops
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
